import React, { useState } from "react";
import "./RightSidebar.css";

import { FaChevronCircleLeft, FaChevronCircleRight } from "react-icons/fa";
import { FiMoreVertical } from "react-icons/fi";
import {
    MdEditNote,
    MdDelete,
    MdArchive,
    MdLocationOn,
    MdClose,
} from "react-icons/md";

import MeasuringPoints from "../../utils/MeasuringPoints/MeasuringPoints";
import Substations from "../../utils/Substations/Substations.js";
import PowerLines from "../../utils/PowerLines/PowerLines.js";
import Saidi from "../../utils/saidi/Saidi.js";
import Saifi from "../../utils/saifi/Saifi.js";
import ActiveSubstations from "../../utils/Active substations/ActiveSubstations.js";
import SubstationsLoss from "../../utils/Substation loss/SubstationsLoss.js";
import RegionOfInterestInfo from "../RegionOfInterest/RegionOfInterestInfo.js";
import UpdateForm from "../../utils/update/UpdateForm.js";
import GetFeatureInfo from "../../utils/GetFeatureInfo/GetFeatureInfo.js";
import ReadingWalkPanel from "../ReadingWalk/ReadingWalkPanel.js";
import { showMapConfirmToast } from "../../utils/Toast/mapToast";

import {
    deleteFeature,
    archiveFeature as archiveSelectedFeature,
} from "../../utils/Delete/deleteArchiveActions";

const RightSidebar = ({
    isCollapsed,
    toggleCollapse,
    selectedFeature,
    onRemoveSelectedFeature,
    substationOptions,
    wfsLayers,
    setWfsLayers,
    clickInfo,
    handleSubstationsLossInformation,
    regionStubovi,
    regionTrafostanice,
    regionVodovi,
    activeMode,
    setActiveMode,
    handleUpdateAttributeWrapper,
    handleUpdateGeometryWrapper,
    setPopupInfo,
    setCurrentDrawing,
    popupInfo,
    currentDrawing,
    typeName,
    setIsDeleted,
    setOverlay,
    isOhActive,
    networkType,
    viewState,
    setViewState,
}) => {
    const [isUpdateFormOpen, setIsUpdateFormOpen] = useState(false);
    const [selectedRange, setSelectedRange] = useState(null);
    const [openMenuFeatureId, setOpenMenuFeatureId] = useState(null);

    const flatSelectedFeatures = Array.isArray(selectedFeature)
        ? selectedFeature.flat().filter(Boolean)
        : selectedFeature
            ? [selectedFeature]
            : [];

    const firstFeature = flatSelectedFeatures.find((f) => f?.id) || null;

    const isStatisticsOnly = firstFeature
        ? !firstFeature.id.includes("mjerna_mjesta")
        : true;

    const measuringPointsFeatures = flatSelectedFeatures.filter((f) =>
        f?.id?.includes("mjerna_mjesta")
    );

    const trafoFeatures = flatSelectedFeatures.filter((f) =>
        f?.id?.includes("trafo_stanice")
    );

    const otherFeatures = flatSelectedFeatures.filter(
        (f) =>
            !f?.id?.includes("mjerna_mjesta") &&
            !f?.id?.includes("trafo_stanice")
    );

    const isTrafoLayerActive = wfsLayers?.some((layer) =>
        layer?.id?.includes("trafo_stanice")
    );

    const getFeatureKey = (feature, index) => {
        return feature?.id || `${feature?.properties?.id || "feature"}-${index}`;
    };

    const closeFeatureMenu = () => {
        setOpenMenuFeatureId(null);
    };

    const prepareFeatureForAction = (feature) => {
        setCurrentDrawing(feature);
        setPopupInfo(feature);
    };

    const startUpdateAttribute = (feature) => {
        prepareFeatureForAction(feature);
        setActiveMode("updateAttribute");
        setIsUpdateFormOpen(true);
        closeFeatureMenu();
    };

    const startUpdateGeometry = (feature) => {
        prepareFeatureForAction(feature);
        setActiveMode("updateGeometry");
        setIsUpdateFormOpen(true);
        closeFeatureMenu();
    };

    const startDelete = (feature) => {
        prepareFeatureForAction(feature);
        closeFeatureMenu();

        showMapConfirmToast({
            title: "Potvrda brisanja",
            message: "Da li ste sigurni da želite trajno obrisati ovaj objekat?",
            confirmText: "Obriši",
            cancelText: "Odustani",
            variant: "danger",
            autoClose: false,
            onConfirm: () =>
                deleteFeature({
                    feature,
                    setIsDeleted,
                    setCurrentDrawing,
                }),
        });
    };

    const archiveFeature = (feature) => {
        prepareFeatureForAction(feature);
        closeFeatureMenu();

        showMapConfirmToast({
            title: "Potvrda arhiviranja",
            message: "Da li ste sigurni da želite arhivirati ovaj objekat?",
            confirmText: "Arhiviraj",
            cancelText: "Odustani",
            variant: "archive",
            autoClose: false,
            onConfirm: () =>
                archiveSelectedFeature({
                    feature,
                    setIsDeleted,
                    setCurrentDrawing,
                }),
        });
    };

    const removeFromSelection = (feature) => {
        if (typeof onRemoveSelectedFeature === "function") {
            onRemoveSelectedFeature(feature);
        }

        closeFeatureMenu();
    };

    const flattenCoordinates = (coordinates) => {
        if (!Array.isArray(coordinates)) return [];

        if (
            typeof coordinates[0] === "number" &&
            typeof coordinates[1] === "number"
        ) {
            return [coordinates];
        }

        return coordinates.flatMap(flattenCoordinates);
    };

    const findFeatureOnMap = (feature) => {
        const coordinates = feature?.geometry?.coordinates;

        if (!coordinates || typeof setViewState !== "function") {
            closeFeatureMenu();
            return;
        }

        const points = flattenCoordinates(coordinates);

        if (!points.length) {
            closeFeatureMenu();
            return;
        }

        const longitude =
            points.reduce((sum, point) => sum + Number(point[0]), 0) /
            points.length;

        const latitude =
            points.reduce((sum, point) => sum + Number(point[1]), 0) /
            points.length;

        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
            closeFeatureMenu();
            return;
        }

        setViewState((prev) => ({
            ...prev,
            longitude,
            latitude,
            zoom: Math.max(prev?.zoom || 14, 17),
        }));

        closeFeatureMenu();
    };

    const FeatureActionsButton = ({ feature, index }) => {
        const featureKey = getFeatureKey(feature, index);
        const isOpen = openMenuFeatureId === featureKey;

        const toggleMenu = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpenMenuFeatureId(isOpen ? null : featureKey);
        };

        const handleActionClick = (e, action) => {
            e.preventDefault();
            e.stopPropagation();
            action(feature);
        };

        return (
            <div className="feature-actions-wrapper">
                <button
                    type="button"
                    className={`feature-actions-button ${isOpen ? "active" : ""}`}
                    onClick={toggleMenu}
                    title="Opcije"
                    aria-label="Opcije"
                >
                    <FiMoreVertical />
                </button>

                {isOpen && (
                    <div
                        className="feature-actions-menu"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button type="button" onClick={(e) => handleActionClick(e, startUpdateAttribute)}>
                            <MdEditNote /> <span>Ažuriranje atributa</span>
                        </button>

                        <button type="button" onClick={(e) => handleActionClick(e, startUpdateGeometry)}>
                            <MdEditNote /> <span>Ažuriranje geometrije</span>
                        </button>

                        <button type="button" onClick={(e) => handleActionClick(e, startDelete)}>
                            <MdDelete /> <span>Brisanje</span>
                        </button>

                        <button type="button" onClick={(e) => handleActionClick(e, archiveFeature)}>
                            <MdArchive /> <span>Arhiviranje</span>
                        </button>

                        <button type="button" onClick={(e) => handleActionClick(e, removeFromSelection)}>
                            <MdClose /> <span>Ukloni iz selekcije</span>
                        </button>

                        <button type="button" onClick={(e) => handleActionClick(e, findFeatureOnMap)}>
                            <MdLocationOn /> <span>Pronađi na mapi</span>
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const isSelectionMode =
        activeMode === "select" ||
        activeMode === "selectRectangle" ||
        activeMode === "selectCircle";

    return (
        <aside
            className={`right-sidebar ${isCollapsed ? "collapsed" : isStatisticsOnly ? "narrow" : "wide"
                }`}
        >
            <div className="content">
                {!isCollapsed && (
                    <>
                        {wfsLayers?.some((layer) =>
                            layer?.id?.includes(`dionica-${networkType}-layer`)
                        ) ? (
                            <PowerLines
                                wfsLayers={wfsLayers}
                                setWfsLayers={setWfsLayers}
                                networkType={networkType}
                            />
                        ) : null}

                        {activeMode === "regionOfInterest" ? (
                            <RegionOfInterestInfo
                                regionStubovi={regionStubovi}
                                regionTrafostanice={regionTrafostanice}
                                regionVodovi={regionVodovi}
                                setOverlay={setOverlay}
                            />
                        ) : null}

                        {isTrafoLayerActive &&
                            substationOptions?.broj_korisnika_ts?.checked ? (
                            <Substations
                                wfsLayers={wfsLayers}
                                setWfsLayers={setWfsLayers}
                                selectedRange={selectedRange}
                                setSelectedRange={setSelectedRange}
                            />
                        ) : null}

                        {substationOptions?.saidi_ts?.checked ? (
                            <Saidi
                                wfsLayers={wfsLayers}
                                clickInfo={clickInfo}
                                selectedFeature={selectedFeature}
                            />
                        ) : null}

                        {substationOptions?.saifi_ts?.checked ? (
                            <Saifi
                                wfsLayers={wfsLayers}
                                clickInfo={clickInfo}
                                selectedFeature={selectedFeature}
                            />
                        ) : null}

                        {substationOptions?.aktivne_ts?.checked ? (
                            <ActiveSubstations
                                clickInfo={clickInfo}
                                selectedFeature={selectedFeature}
                            />
                        ) : null}

                        {substationOptions?.gubici_ts?.checked ? (
                            <SubstationsLoss
                                clickInfo={clickInfo}
                                selectedFeature={selectedFeature}
                                wfsLayers={wfsLayers}
                                setWfsLayers={setWfsLayers}
                                handleSubstationsLossInformation={handleSubstationsLossInformation}
                                setOverlay={setOverlay}
                            />
                        ) : null}

                        {isSelectionMode && measuringPointsFeatures.length > 0 ? (
                            <MeasuringPoints selectedFeatures={measuringPointsFeatures} />
                        ) : null}

                        {isSelectionMode && trafoFeatures.length > 0 ? (
                            <GetFeatureInfo
                                selectedFeatures={trafoFeatures}
                                onRemoveSelectedFeature={onRemoveSelectedFeature}
                                renderActions={(feature, index) => (
                                    <FeatureActionsButton feature={feature} index={index} />
                                )}
                            />
                        ) : null}

                        {isSelectionMode && otherFeatures.length > 0 ? (
                            <GetFeatureInfo
                                selectedFeatures={otherFeatures}
                                onRemoveSelectedFeature={onRemoveSelectedFeature}
                                renderActions={(feature, index) => (
                                    <FeatureActionsButton feature={feature} index={index} />
                                )}
                            />
                        ) : null}

                        {(activeMode === "updateAttribute" ||
                            activeMode === "updateGeometry") &&
                            flatSelectedFeatures.length > 0 ? (
                            <UpdateForm
                                activeMode={activeMode}
                                currentDrawing={currentDrawing}
                                handleUpdateAttributeWrapper={handleUpdateAttributeWrapper}
                                handleUpdateGeometryWrapper={handleUpdateGeometryWrapper}
                                setPopupInfo={setPopupInfo}
                                setCurrentDrawing={setCurrentDrawing}
                                popupInfo={popupInfo}
                                isOpen={isUpdateFormOpen}
                                onRequestClose={() => {
                                    setIsUpdateFormOpen(false);
                                    setActiveMode("select");
                                }}
                                typeName={typeName}
                            />
                        ) : null}

                        {isOhActive ? (
                            <ReadingWalkPanel
                                setOverlay={setOverlay}
                                setWfsLayers={setWfsLayers}
                            />
                        ) : null}
                    </>
                )}
            </div>

            <div className="toggle-button" onClick={toggleCollapse}>
                {isCollapsed ? (
                    <FaChevronCircleLeft className="icon" />
                ) : (
                    <FaChevronCircleRight className="icon" />
                )}

                <span className="text">
                    {isCollapsed ? "Proširi" : "Sakrij"}
                </span>
            </div>
        </aside>
    );
};

export default RightSidebar;